import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { AgGridAngular } from 'ag-grid-angular';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ICustomer } from '@ruby/modules/customer-select/models/customer.interface';
import { ITrackMovePayload } from '@ruby/modules/track-move/models/track-move.interface';
import { fromTrackMove, TrackMoveActions, TrackMoveSelectors } from '@ruby/modules/track-move/store';
import { ETrackGridTitle } from '@ruby/shared/enums/track-move.enums';
import { ITrack } from '@ruby/shared/models/inventory-management/inventory-header.interface';

@Component({
  selector: 'ng-from-track-header-container',
  templateUrl: './from-track-header.container.html',
  styleUrls: ['./from-track-header.container.scss']
})
export class FromTrackHeaderContainerComponent implements OnChanges {
  @Input() fromTrackGrid!: AgGridAngular;
  @Input() toTrackGrid!: AgGridAngular;
  @Output() clearTracksOrder = new EventEmitter();
  selectedFromTrack?: ITrack;
  customerSelected!: ICustomer;
  userRacf!: string;
  store$: Observable<{ trackMoveState: fromTrackMove.State; customerSelected?: ICustomer; userRacf?: string; performTrackMoves?: boolean }>;

  constructor(private store: Store<fromTrackMove.State>) {
    this.store$ = this.store.select(TrackMoveSelectors.selectTrackMoveWithAuth).pipe(
      tap((st: { customerSelected?: ICustomer; userRacf?: string; trackMoveState: fromTrackMove.State }) => {
        if (st.customerSelected && (this.customerSelected !== st.customerSelected) && st.userRacf) {
          this.customerSelected = st.customerSelected;
          this.userRacf = st.userRacf;
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.fromTrackGrid && changes.fromTrackGrid.currentValue) {
      this.fromTrackGrid.api.addEventListener('rowDragEnd', this.dispatchTrackEquipments.bind(this, ETrackGridTitle.FROM_TRACK));
    }

    if (changes.toTrackGrid && changes.toTrackGrid.currentValue) {
      this.toTrackGrid.api.addEventListener('rowDragEnd', this.dispatchTrackEquipments.bind(this, ETrackGridTitle.TO_TRACK));
    }
  }

  /**
   * Dispatch Selected Track
   *
   * @summary Dispatches selected track
   * @param $track: ITrack
   * @returns void
   */
  dispatchSelectedTracK($track: ITrack): void {
    this.selectedFromTrack = $track;
    this.clearTracksOrder.emit();
    this.store.dispatch(TrackMoveActions.setFromTrack({ data: $track }));
    this.store.dispatch(TrackMoveActions.loadTrackDetails({
      data: {
        classCode: this.customerSelected.locations[0].classCode,
        stationCode: this.customerSelected.locations[0].stationCode,
        trackID: $track.id,
        trackName: $track.name,
        mainTrack: $track.mainTrack
      } as ITrackMovePayload
    }));
  }

  /**
   * Dispatch Move
   *
   * @summary Dispatches selected inventory
   * @param $payload: ITrackMovePayload
   * @returns void
   */
  dispatchMove($payload: ITrackMovePayload): void {
    $payload.classCode = this.customerSelected.locations[0].classCode;
    $payload.stationCode = this.customerSelected.locations[0].stationCode;
    this.store.dispatch(TrackMoveActions.moveTrack({ data: $payload }));
  }

  /**
   * Dispatch Adjust
   *
   * @summary Dispatches selected inventory
   * @param $request: { fromTrackEquipments: Array<string>, toTrackEquipments: Array<string>, payload: ITrackMovePayload }
   * @returns void
   */
  dispatchAdjust($request: { fromTrackEquipments: Array<string>; toTrackEquipments: Array<string>; payload: ITrackMovePayload }): void {
    $request.payload.classCode = this.customerSelected.locations[0].classCode;
    $request.payload.stationCode = this.customerSelected.locations[0].stationCode;
    this.store.dispatch(TrackMoveActions.setFromTrackEquipments({ data: $request.fromTrackEquipments }));
    this.store.dispatch(TrackMoveActions.setToTrackEquipments({ data: $request.toTrackEquipments }));
    this.store.dispatch(TrackMoveActions.adjustTrack({ data: $request.payload }));
  }

  /**
   * Dispatch Track Equipments
   *
   * @summary Dispatches track equipments by type
   * @param type: ETrackGridTitle
   * @returns void
   */
  dispatchTrackEquipments(type: ETrackGridTitle): void {
    if (type === ETrackGridTitle.FROM_TRACK) {
      this.store.dispatch(TrackMoveActions.setFromTrackEquipments({ data: [] }));
    } else {
      this.store.dispatch(TrackMoveActions.setToTrackEquipments({ data: [] }));
    }
  }

  /**
   * Dispatch Refresh
   *
   * @summary Dispatches refresh
   * @param $payload: ITrackMovePayload
   * @returns void
   */
  dispatchRefresh($payload: ITrackMovePayload): void {
    $payload.classCode = this.customerSelected.locations[0].classCode;
    $payload.stationCode = this.customerSelected.locations[0].stationCode;
    this.clearTracksOrder.emit();
    this.store.dispatch(TrackMoveActions.refreshTrack({ data: $payload }));
  }
}
